import { nanoid } from '@reduxjs/toolkit';

import type { TemplateSnapshot } from './template.types';

/**
 * Generates a unique template ID
 * @returns Unique nanoid string
 */
export const generateTemplateId = (): string => `template_${nanoid()}`;

/**
 * Validates that a template has the required structure
 * @param template - Template to validate
 * @returns True if valid, false otherwise
 */
export const validateTemplateStructure = (
  template: unknown,
): template is TemplateSnapshot => {
  if (!template || typeof template !== 'object') {
    return false;
  }

  const t = template as Partial<TemplateSnapshot>;

  return !!(
    t.id &&
    t.name &&
    t.blocks &&
    t.widgetInstances &&
    Array.isArray(t.regions) &&
    Array.isArray(t.sections) &&
    typeof t.blocks === 'object' &&
    typeof t.isCustom === 'boolean' &&
    typeof t.widgetInstances === 'object'
  );
};

/**
 * Merges template with defaults for graceful degradation
 * If template references missing widget types, they will be filtered out
 * @param template - Template to merge
 * @returns Template with validated widget instances
 */
export const mergeTemplateWithDefaults = (
  template: TemplateSnapshot,
): TemplateSnapshot => {
  // Filter out any widget instances that don't have corresponding blocks
  const referencedInstanceIds = new Set<string>();

  Object.values(template.blocks).forEach(block => {
    referencedInstanceIds.add(block.id); // Direct block-instance
    if (block.instanceId) {
      referencedInstanceIds.add(block.instanceId); // Multi-instance reference
    }
    // Include additional instances (e.g., new-cases asides in geomap block)
    if (block.additionalInstances) {
      block.additionalInstances.forEach(ai => {
        referencedInstanceIds.add(ai.instanceId);
      });
    }
  });

  const filteredWidgetInstances = Object.fromEntries(
    Object.entries(template.widgetInstances).filter(([widgetId]) =>
      referencedInstanceIds.has(widgetId),
    ),
  );

  return {
    ...template,
    widgetInstances: filteredWidgetInstances,
  };
};
